import type React from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/atoms/Buttons';
import FormField from '@/components/atoms/FormField';
import TextField from '@/components/atoms/TextField';
import { ModalBody, ModalComponent, ModalFooter } from '@/components/molecules/ModalComponent';
import type { CreateNewContractInput as UpdateContractInput } from '@/domain/licenses';

import styles from './ModalUpdateBioscopeContract.module.scss';

export interface ModalUpdateBioscopeContractProps {
    onSubmitted: (contractId: number, ev: UpdateContractInput) => void;
    contractData: {
        id: number;
        clientId: number;
        count: number;
        description: string;
    };
}

function ModalUpdateBioscopeContract({
    contractData,
    onSubmitted,
}: ModalUpdateBioscopeContractProps) {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    const toggle = () => setOpen(!open);

    const [formData, setFormData] = useState<UpdateContractInput>({
        ...contractData,
    });

    function updateForm<T extends keyof UpdateContractInput>(key: T) {
        return (value: UpdateContractInput[T]) =>
            setFormData((formData) => ({ ...formData, [key]: value }));
    }

    const onFormDataOnSubmit = (ev: React.FormEvent<HTMLFormElement>) => {
        onSubmitted(contractData.id, formData);
        setOpen(false);
        ev.preventDefault();
    };

    return (
        <>
            <Button
                id="openUpdateBioscopeContractModal"
                data-testid="openUpdateBioscopeContractModal"
                className={styles.openButton}
                onClick={() => setOpen(true)}>
                {t('common.actions.update')}
            </Button>
            <ModalComponent
                isOpen={open}
                onClose={toggle}
                centered={false}
                title={t('bioscopeContracts.updateModal.title')}>
                <form onSubmit={onFormDataOnSubmit}>
                    <ModalBody>
                        <FormField
                            label={t('bioscopeContracts.updateModal.bioscopeCount')}
                            htmlFor="ContractNumber">
                            <TextField
                                id="ContractNumber"
                                name="ContractNumber"
                                type="number"
                                value={formData.count}
                                onChange={(ev) =>
                                    updateForm('count')(Number.parseInt(ev.target.value))
                                }
                                placeholder={t('')}
                                required={false}
                            />
                        </FormField>
                        <FormField label={t('molecules.common.description')} htmlFor="description">
                            <TextField
                                id="Description"
                                name="Description"
                                type="text"
                                value={formData.description}
                                onChange={(ev) => updateForm('description')(ev.target.value)}
                                placeholder={t('')}
                                required={false}
                            />
                        </FormField>
                    </ModalBody>
                    <ModalFooter>
                        <Button variant="secondary" onClick={() => setOpen(false)}>
                            {t('common.actions.cancel')}
                        </Button>
                        <Button variant="primary" type="submit">
                            {t('common.actions.update')}
                        </Button>
                    </ModalFooter>
                </form>
            </ModalComponent>
        </>
    );
}

export default ModalUpdateBioscopeContract;
export { ModalUpdateBioscopeContract };
